import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Filter } from "lucide-react";
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle, 
  SheetTrigger,
  SheetClose
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar-rac";
import { getLocalTimeZone, today, CalendarDate } from "@internationalized/date";
import type { DateValue } from "react-aria-components";
import { cn } from "@/lib/utils";

// Add import for Select components
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  "Bouquet",
  "Floral Arrangement",
  "Potted Plant",
  "Single Flower",
  "Other"
] as const;

const OCCASIONS = [
  "Birthday",
  "Thank You",
  "Love & Romance",
  "Sympathy",
  "Just Because"
] as const;

interface MobileFilterDropdownProps {
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  selectedOccasions: string[];
  setSelectedOccasions: (occasions: string[]) => void;
  priceRange: [number, number];
  setPriceRange: (range: [number, number]) => void;
  selectedDate: Date | null;
  handleDateSelect: (value: DateValue | null) => void;
  deliveryType: 'delivery' | 'pickup';
  toggleDeliveryType: (type: 'delivery' | 'pickup') => void;
  deliveryCount: number;
  pickupCount: number;
  categoryCountMap?: Record<string, number>;
  occasionCountMap?: Record<string, number>;
  sortOrder?: string;
  handleSortOrderChange?: (value: string) => void;
  groupBySizeVariant?: boolean;
  setGroupBySizeVariant?: (value: boolean) => void;
}

export const MobileFilterDropdown = ({
  selectedCategories,
  setSelectedCategories,
  selectedOccasions,
  setSelectedOccasions,
  priceRange,
  setPriceRange,
  selectedDate,
  handleDateSelect,
  deliveryType,
  toggleDeliveryType,
  deliveryCount,
  pickupCount,
  categoryCountMap = {},
  occasionCountMap = {},
  sortOrder = 'default',
  handleSortOrderChange = () => {},
  groupBySizeVariant = true,
  setGroupBySizeVariant = () => {}
}: MobileFilterDropdownProps) => {
  const [open, setOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Format the displayed date
  const formatDate = (date: Date | null) => {
    if (!date) return 'Any Date';
    return format(date, 'PPP');
  };
  
  const dateToCalendarDate = (date: Date | null) => {
    if (!date) return null;
    return new CalendarDate(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
  };

  const formatPriceRange = () => {
    if (priceRange[1] === 500) {
      return `$${priceRange[0]} - $500+`;
    }
    return `$${priceRange[0]} - $${priceRange[1]}`;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <div data-filter-trigger="true">
          <Button 
            variant="outline" 
            className="hidden"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </Button>
        </div>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-[85vh] pt-6 px-4 bg-white overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle className="text-brand-primary text-xl">Filters</SheetTitle>
        </SheetHeader>
        
        <div className="space-y-6">
          {/* Sort By Dropdown - Add this section */}
          <div>
            <h3 className="text-[16px] font-semibold text-brand-secondary mb-3">Sort by</h3>
            <Select value={sortOrder} onValueChange={handleSortOrderChange}>
              <SelectTrigger className="w-full bg-white border-2 border-gray-300 h-[42px] text-[15px] rounded-lg hover:border-brand-secondary text-brand-secondary">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Recommended</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Group by Product Toggle */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-[16px] font-semibold text-brand-secondary mb-1">Group by Product</h3>
                <p className="text-[13px] text-brand-primary/60">
                  {groupBySizeVariant ? 'Showing one card per product' : 'Showing all size variants'}
                </p>
              </div>
              <button
                onClick={() => setGroupBySizeVariant(!groupBySizeVariant)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#843329] focus:ring-offset-2 ${
                  groupBySizeVariant ? 'bg-[#843329]' : 'bg-gray-400'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                    groupBySizeVariant ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          
          {/* Delivery Type */}
          <div>
            <h3 className="text-[16px] font-semibold text-brand-secondary mb-3">Delivery Type</h3>
            <div className="space-y-2">
              <Button
                onClick={() => toggleDeliveryType('delivery')}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-2 bg-white border-2 rounded-lg transition-colors",
                  deliveryType === 'delivery' 
                    ? "bg-[#967F81]/30 border-[#967F81]" 
                    : "border-gray-300 hover:border-[#843329]"
                )}
              >
                <span className={cn(
                  "text-[15px] font-normal",
                  deliveryType === 'delivery' ? "text-[#2C1810]" : "text-gray-700"
                )}>Delivery</span>
                <span className={cn(
                  "text-[13px] font-normal",
                  deliveryType === 'delivery' ? "text-[#2C1810]" : "text-gray-500"
                )}>{deliveryCount > 0 ? `${deliveryCount}+` : '...'}</span>
              </Button>
              
              <Button
                onClick={() => toggleDeliveryType('pickup')}
                className={cn(
                  "w-full flex items-center justify-between px-4 py-2 bg-white border-2 rounded-lg transition-colors",
                  deliveryType === 'pickup' 
                    ? "bg-[#967F81]/30 border-[#967F81]" 
                    : "border-gray-300 hover:border-[#843329]"
                )}
              >
                <span className={cn(
                  "text-[15px] font-normal",
                  deliveryType === 'pickup' ? "text-[#2C1810]" : "text-gray-700"
                )}>Pickup</span>
                <span className={cn(
                  "text-[13px] font-normal",
                  deliveryType === 'pickup' ? "text-[#2C1810]" : "text-gray-500"
                )}>{pickupCount > 0 ? `${pickupCount}+` : '...'}</span>
              </Button>
            </div>
          </div>
          
          {/* Date Selector */}
          <div>
            <h3 className="text-[16px] font-semibold text-brand-secondary mb-3">Delivery Date</h3>
            <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal bg-white border-2 h-[42px] text-[13px] rounded-lg transition-colors",
                    selectedDate 
                      ? "bg-[#967F81]/30 border-[#967F81] text-[#2C1810]" 
                      : "border-gray-300 hover:border-[#843329] text-[#2C1810]"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3.5 w-3.5 text-[#2C1810]" />
                  {formatDate(selectedDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white border-2 border-brand-secondary shadow-lg rounded-lg" align="start">
                <div className="flex flex-col">
                  <button
                    onClick={() => {
                      handleDateSelect(null);
                      setIsCalendarOpen(false);
                    }}
                    className="w-full px-4 py-2 text-[13px] font-normal text-brand-secondary hover:bg-[#843329]/20 border-b-2 border-brand-secondary"
                  >
                    Any Date
                  </button>
                  <div className="p-2">
                    <Calendar
                      value={dateToCalendarDate(selectedDate)}
                      onChange={(value) => {
                        handleDateSelect(value);
                        setIsCalendarOpen(false);
                      }}
                      minValue={today(getLocalTimeZone())}
                      className="[&_[aria-selected]]:bg-[#967F81] [&_[aria-selected]]:text-brand-secondary [&_[aria-selected]]:font-medium hover:[&_[aria-selected]]:bg-[#967F81]/90 [&_button:hover]:bg-[#967F81]/10"
                    />
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Price Range */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-semibold text-brand-secondary">Budget</h3>
              <span className="text-[13px] text-brand-secondary font-normal">{formatPriceRange()}</span>
            </div>
            <Slider
              defaultValue={[0, 1000]}
              value={priceRange}
              onValueChange={(value) => setPriceRange(value as [number, number])}
              min={0}
              max={1000}
              step={25}
              minStepsBetweenThumbs={1}
              className="[&_[data-orientation=horizontal]]:h-2 [&_[data-orientation=horizontal]]:bg-gray-300 [&_span[role=slider]]:h-5 [&_span[role=slider]]:w-5 [&_span[role=slider]]:bg-[#967F81] [&_span[role=slider]]:border-2 [&_span[role=slider]]:border-white [&_span[role=slider]]:shadow-md [&_[data-orientation=horizontal]_[role=slider]~[role=slider]~div]:bg-[#967F81]"
            />
          </div>
          
          {/* Categories */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-semibold text-brand-secondary">Categories</h3>
              <button 
                onClick={() => {
                  if (selectedCategories.length === CATEGORIES.length) {
                    setSelectedCategories([]);
                  } else {
                    setSelectedCategories([...CATEGORIES]);
                  }
                }}
                className="text-[13px] text-brand-secondary hover:text-brand-secondary underline font-normal"
              >
                {selectedCategories.length === CATEGORIES.length ? 'Clear' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {CATEGORIES.map((category) => (
                <div key={category} className={cn(
                  "flex items-center justify-between group cursor-pointer p-3 rounded-lg border-2 transition-colors",
                  selectedCategories.includes(category) 
                    ? "bg-[#967F81]/30 border-[#967F81]" 
                    : "bg-white border-gray-300 hover:border-[#843329]"
                )}>
                  <label className="flex items-center gap-3 flex-1 cursor-pointer">
                    <Checkbox
                      checked={selectedCategories.includes(category)}
                      onCheckedChange={(checked) => {
                        const newCategories = checked
                          ? [...selectedCategories, category]
                          : selectedCategories.filter(c => c !== category);
                        setSelectedCategories(newCategories);
                      }}
                      className="h-4 w-4 border-2 border-gray-400 rounded-sm data-[state=checked]:bg-[#2C1810] data-[state=checked]:border-[#2C1810] data-[state=checked]:text-white"
                    />
                    <span className={cn(
                      "text-sm font-medium",
                      selectedCategories.includes(category) ? "text-[#2C1810]" : "text-gray-700"
                    )}>{category}</span>
                  </label>
                  <span className={cn(
                    "text-xs",
                    selectedCategories.includes(category) ? "text-[#2C1810]" : "text-gray-500"
                  )}>{categoryCountMap[category] || 0}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Occasions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[16px] font-semibold text-brand-secondary">Occasions</h3>
              <button 
                onClick={() => {
                  if (selectedOccasions.length === OCCASIONS.length) {
                    setSelectedOccasions([]);
                  } else {
                    setSelectedOccasions([...OCCASIONS]);
                  }
                }}
                className="text-[13px] text-brand-secondary hover:text-brand-secondary underline font-normal"
              >
                {selectedOccasions.length === OCCASIONS.length ? 'Clear' : 'Select All'}
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {OCCASIONS.map((occasion) => (
                <div key={occasion} className={cn(
                  "flex items-center justify-between group cursor-pointer p-3 rounded-lg border-2 transition-colors",
                  selectedOccasions.includes(occasion) 
                    ? "bg-[#967F81]/30 border-[#967F81]" 
                    : "bg-white border-gray-300 hover:border-[#843329]"
                )}>
                  <label className="flex items-center gap-3 flex-1 cursor-pointer">
                    <Checkbox
                      checked={selectedOccasions.includes(occasion)}
                      onCheckedChange={(checked) => {
                        const newOccasions = checked
                          ? [...selectedOccasions, occasion]
                          : selectedOccasions.filter(o => o !== occasion);
                        setSelectedOccasions(newOccasions);
                      }}
                      className="h-4 w-4 border-2 border-gray-400 rounded-sm data-[state=checked]:bg-[#2C1810] data-[state=checked]:border-[#2C1810] data-[state=checked]:text-white"
                    />
                    <span className={cn(
                      "text-sm font-medium",
                      selectedOccasions.includes(occasion) ? "text-[#2C1810]" : "text-gray-700"
                    )}>{occasion}</span>
                  </label>
                  <span className={cn(
                    "text-xs",
                    selectedOccasions.includes(occasion) ? "text-[#2C1810]" : "text-gray-500"
                  )}>{occasionCountMap[occasion] || 0}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Apply button */}
          <div className="sticky bottom-0 pb-16 pt-2 bg-white">
            <SheetClose asChild>
              <Button 
                className="w-full bg-[#967F81] hover:bg-[#843329] text-brand-secondary font-semibold rounded-lg h-12"
              >
                Apply Filters
              </Button>
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}; 